/**
 * Platform usage API client (SF-5095; the company route SF-5096; SF-5117 adds progress, daily
 * averages, the changes route and Eva).
 *
 * One place for the sf-api `/admin/usage/*` routes and their response shapes, so the dashboard
 * and the company Health tab read the same types. The routes need `admin:companies:read`.
 *
 * The data provider unwraps sf-api's `{ success, data }` envelope, so hook consumers read the
 * payload from `query.data?.data`. Every hook turns off the global error toast: each screen
 * section shows its own error, and one failed section must not look like the whole page failed.
 */
import { useCustom } from '@refinedev/core';

/** Base path for the usage read routes on sf-api. */
export const USAGE_BASE = 'admin/usage';

/** The survey type keys, in the order the screens list them. */
export const SURVEY_TYPE_KEYS = [
  'audit',
  'mystery_visit',
  'help_desk',
  'learning',
  'client_connect',
  'csat',
  'generic',
] as const;

export type SurveyTypeKey = (typeof SURVEY_TYPE_KEYS)[number];

/** Every usage type key: the survey types plus old style ratings. */
export type UsageTypeKey = SurveyTypeKey | 'ratings';

/** Feedback counts for one scope and month. */
export interface UsageTotals {
  total_feedback: number;
  surveys: { total: number; by_type: Record<SurveyTypeKey, number> };
  ratings: number;
}

/** A company in a platform list, with its figures for the month. */
export interface UsageCompanyRow {
  company_id: string;
  name: string;
  sales_channel: string | null;
  totals: UsageTotals;
}

/** A company whose feedback fell against last month by at least the drop rule. */
export interface UsageDropRow {
  company_id: string;
  name: string;
  total_feedback: number;
  previous_total_feedback: number;
  change_pct: number;
}

/**
 * How far into the month the figures go (SF-5117). For the month in progress (Europe/London)
 * the figures cover full local days 1..days_counted only; `through` is the last day counted, or
 * null when no full day is counted yet.
 */
export interface UsageProgress {
  in_progress: boolean;
  days_counted: number;
  days_in_month: number;
  through: string | null;
}

/**
 * What the previous month figures cover: the same days of last month while this month is in
 * progress, otherwise the whole month.
 */
export type PreviousScope = 'same_days' | 'full_month';

/** Figures per counted day, to 1 decimal; null when no full day is counted (SF-5117). */
export interface DailyAverage {
  total_feedback: number;
  surveys: number;
  ratings: number;
}

/** GET /admin/usage/platform. */
export interface PlatformUsage {
  period: string;
  computed_at: string | null;
  rule: { drop_pct: number; min_previous: number };
  active_companies: {
    total: number;
    enterprise: number;
    self_serve: number;
    /** SF-5117 (optional: an older API leaves these out). Active in the previous period. */
    previous_total?: number;
    /** Active this period, no feedback in the whole previous month. */
    new?: number;
    /** Feedback in the whole previous month, none so far this period. */
    gone?: number;
  };
  totals: UsageTotals;
  /** Like-for-like with `totals` (see previous_scope). */
  previous_totals: UsageTotals;
  /** SF-5117 fields: optional so the screen still works against an older API. */
  progress?: UsageProgress;
  previous_scope?: PreviousScope;
  daily_average?: DailyAverage | null;
  previous_daily_average?: DailyAverage | null;
  /** The ServeFirst Live company, never part of the customer figures. */
  internal: { company_id: string; name: string; totals: UsageTotals } | null;
  top_companies: UsageCompanyRow[];
  /** Largest drop first, capped by the API at 25 rows. */
  biggest_drops: UsageDropRow[];
  /** How many companies matched the rule before the cap. */
  biggest_drops_total: number;
  /**
   * SF-5124 (optional: an older API leaves it out, and the Eva tile is left out). Companies
   * using Eva, same rule as /features eva.companies_using; previous is like-for-like.
   */
  eva?: { companies_using: number; previous_companies_using?: number };
}

/** A company that started giving feedback this period (SF-5117). */
export interface NewlyActiveRow {
  company_id: string;
  name: string;
  sales_channel: string | null;
  total_feedback: number;
}

/** A company with feedback in the whole previous month and none so far this period. */
export interface NewlyInactiveRow {
  company_id: string;
  name: string;
  sales_channel: string | null;
  previous_total_feedback: number;
}

/** GET /admin/usage/platform/changes (SF-5117): customers only, lists uncapped. */
export interface PlatformChanges {
  period: string;
  progress: UsageProgress;
  newly_active: NewlyActiveRow[];
  newly_inactive: NewlyInactiveRow[];
  newly_active_total: number;
  newly_inactive_total: number;
}

/** GET /admin/usage/platform/series: customers only, oldest month first. */
export interface PlatformUsageSeries {
  months: PlatformSeriesMonth[];
}

/**
 * Active customer companies in a month by group (SF-5124). The groups always add up to total,
 * which matches the Active companies tile for that month.
 */
export interface ActiveByGroup {
  total: number;
  enterprise: number;
  self_serve_paid: number;
  free: number;
}

/** One month of the platform series. SF-5124 fields are optional for an older API. */
export interface PlatformSeriesMonth {
  period: string;
  totals: UsageTotals;
  active?: ActiveByGroup;
  eva_companies_using?: number;
}

/** One feature's take-up. The switched-on figures are null where no switch exists. */
export interface FeatureUsageRow {
  type: UsageTypeKey;
  companies_using: number;
  companies_switched_on: number | null;
  switched_on_not_used: number | null;
}

/** GET /admin/usage/features. */
export interface FeatureUsage {
  period: string;
  computed_at: string | null;
  features: FeatureUsageRow[];
  /** The switch figures arrive with SF-5117; an older API sends only companies_using. */
  eva: { companies_using: number; switched_on?: number; switched_on_not_used?: number };
}

/** GET /admin/usage/ratings-migration. */
export interface RatingsMigration {
  period: string;
  computed_at: string | null;
  counts: { ratings_only: number; both: number; fully_moved: number };
  ratings_only: Array<{ company_id: string; name: string; ratings: number }>;
  both: Array<{ company_id: string; name: string; ratings: number; surveys: number }>;
  share_by_month: Array<{
    period: string;
    ratings: number;
    total_feedback: number;
    ratings_share: number;
  }>;
  most_ratings_left: Array<{ company_id: string; name: string; ratings: number }>;
}

/** Why a company is left out of the platform figures; null when it is counted. */
export type NotCountedReason = 'demo' | 'test' | 'inactive' | 'internal';

/** One feature for one company: switched_on is null where the feature has no switch. */
export interface CompanyFeatureRow {
  type: UsageTypeKey;
  switched_on: boolean | null;
  used: boolean;
}

/** One local day of a company's feedback. */
export interface CompanyUsageDay {
  date: string;
  total_feedback: number;
  surveys: number;
  ratings: number;
}

/**
 * GET /admin/usage/company/:id. The figures come back even when the company is not counted;
 * the screen decides what to show.
 */
export interface CompanyUsage {
  company: {
    id: string;
    name: string;
    account_type: string | null;
    sales_channel: string | null;
    is_active: boolean;
  };
  counted: boolean;
  not_counted_reason: NotCountedReason | null;
  period: string;
  computed_at: string | null;
  this_month: UsageTotals;
  last_month: UsageTotals;
  monthly: Array<{ period: string; totals: UsageTotals }>;
  daily: CompanyUsageDay[];
  features: CompanyFeatureRow[];
  on_old_ratings: boolean;
  /** SF-5117 fields: optional so the tab still works against an older API. */
  progress?: UsageProgress;
  last_month_scope?: PreviousScope;
  this_month_daily_average?: DailyAverage | null;
  last_month_daily_average?: DailyAverage | null;
  eva?: CompanyEvaUsage;
}

/** Eva messages and cost for one month (cost in USD, to 2 decimals). */
export interface EvaMonthUsage {
  messages: number;
  cost_usd: number;
}

/** A company's Eva switch and use (SF-5117); last_month is like-for-like while in progress. */
export interface CompanyEvaUsage {
  switched_on: boolean;
  used: boolean;
  this_month: EvaMonthUsage;
  last_month: EvaMonthUsage;
}

/**
 * Query options for the month-based hooks. Refine keeps the previous query's data while a new
 * key loads; here that would show last month's figures as this month's after a month change,
 * so a new month starts from the loading state instead.
 */
function monthQueryOptions(options: EnabledOption) {
  return { enabled: options.enabled ?? true, keepPreviousData: false };
}

interface EnabledOption {
  /** Gate the request (e.g. on the permission check). Defaults to true. */
  enabled?: boolean;
}

/** Platform figures for a "YYYY-MM" month. */
export function usePlatformUsage(period: string, options: EnabledOption = {}) {
  return useCustom<PlatformUsage>({
    url: `/${USAGE_BASE}/platform`,
    method: 'get',
    config: { query: { period } },
    queryOptions: monthQueryOptions(options),
    errorNotification: false,
  });
}

/** Companies that started or stopped giving feedback in a "YYYY-MM" month (SF-5117). */
export function usePlatformChanges(period: string, options: EnabledOption = {}) {
  return useCustom<PlatformChanges>({
    url: `/${USAGE_BASE}/platform/changes`,
    method: 'get',
    config: { query: { period } },
    queryOptions: monthQueryOptions(options),
    errorNotification: false,
  });
}

/** Customer totals for the last `months` months, ending this month. */
export function usePlatformUsageSeries(months: number, options: EnabledOption = {}) {
  return useCustom<PlatformUsageSeries>({
    url: `/${USAGE_BASE}/platform/series`,
    method: 'get',
    config: { query: { months } },
    queryOptions: { enabled: options.enabled ?? true },
    errorNotification: false,
  });
}

/** How many companies use each feature in a "YYYY-MM" month. */
export function useFeatureUsage(period: string, options: EnabledOption = {}) {
  return useCustom<FeatureUsage>({
    url: `/${USAGE_BASE}/features`,
    method: 'get',
    config: { query: { period } },
    queryOptions: monthQueryOptions(options),
    errorNotification: false,
  });
}

/** Old style ratings migration for a "YYYY-MM" month, looking back `months` months. */
export function useRatingsMigration(period: string, months: number, options: EnabledOption = {}) {
  return useCustom<RatingsMigration>({
    url: `/${USAGE_BASE}/ratings-migration`,
    method: 'get',
    config: { query: { period, months } },
    queryOptions: monthQueryOptions(options),
    errorNotification: false,
  });
}

/**
 * One company's usage for a "YYYY-MM" month. The company id is in the URL, so it is part of the
 * query key; with keepPreviousData off, moving to another company (the company page is reused
 * across ids) starts from the loading state rather than showing the last company's figures.
 */
export function useCompanyUsage(companyId: string, period: string, options: EnabledOption = {}) {
  return useCustom<CompanyUsage>({
    url: `/${USAGE_BASE}/company/${encodeURIComponent(companyId)}`,
    method: 'get',
    config: { query: { period } },
    queryOptions: monthQueryOptions({ enabled: (options.enabled ?? true) && companyId !== '' }),
    errorNotification: false,
  });
}
